import React, { useMemo } from "react";

import { Stack } from "@mui/material";
import { orderBy } from "lodash";
import { useParams } from "react-router-dom";

import useFartherEmployees from "@fartherfinance/frontend/api/Entity/hooks/useFartherEmployees";
import useGetResourcePermission from "@fartherfinance/frontend/api/Identity/hooks/useGetResourcePermission";
import useListSubjectsWithRelation from "@fartherfinance/frontend/api/Identity/hooks/useListSubjectsWithRelation";
import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import { PersonId } from "@fartherfinance/frontend/api/Investment/types";
import {
  Organization,
  PortfolioPlanId,
} from "@fartherfinance/frontend/api/Types";
import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@src/sharedComponents/Accordion/Accordion";
import Switch from "@src/sharedComponents/Switch/Switch";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";

import { useAccessContext } from "./AccessContextProvider";
import ExpandableRowsAccordion from "./AdvisorRowPermission";
import { LoadingAccordionRow } from "./LoadinState";

const OrganizationAccordion = ({
  organization,
}: {
  organization: Organization;
}) => {
  const {
    organizationsHash,
    organizationSwitch,
    isMutating,
    onSelectedAccordion,
    selectedAccordion,
  } = useAccessContext();

  const { portfolioPlanId } = useParams<{
    portfolioPlanId: PortfolioPlanId;
  }>();
  const {
    color: { $surface, $borderBold },
  } = useTheme();

  const auth = useAdvisorRequestAuth();

  const portfolioPlanDetails = useGetPortfolioPlanById(
    portfolioPlanId,
    {},
    auth
  );

  const hasPublicBasePermission: null | boolean = useMemo(() => {
    if (portfolioPlanDetails.data === undefined) {
      return null;
    }

    const plan = portfolioPlanDetails.data.data.plan;

    switch (plan.basePermission) {
      case "Private":
        return false;

      case "Public-Organization":
        if (plan.organization === organization) {
          return true;
        }
        return false;

      case "Public-Platform":
        return true;
    }
  }, [organization, portfolioPlanDetails.data]);

  const isNotAPrivatePlan: null | boolean = useMemo(() => {
    if (portfolioPlanDetails.data === undefined) {
      return null;
    }

    const plan = portfolioPlanDetails.data.data.plan;

    switch (plan.basePermission) {
      case "Private":
        return false;

      case "Public-Organization":
        return true;

      case "Public-Platform":
        return true;
    }
  }, [portfolioPlanDetails.data]);

  const { data: advisorPermissionData, isLoading: advisorPermissionIsLoading } =
    useGetResourcePermission(
      {
        permission: "view",
        resourceType: "PortfolioPlan",
        resourceId: portfolioPlanId,
        query: {
          organization: organization,
          actorType: "Advisor",
        },
      },
      auth
    );

  const globalPermissionData = useGetResourcePermission(
    {
      permission: "view",
      resourceType: "PortfolioPlan",
      resourceId: portfolioPlanId,
      query: {
        actorType: "Advisor",
      },
    },
    auth
  );

  const { data: orgAccessData, isLoading: orgAccessIsLoading } =
    useListSubjectsWithRelation(
      {
        resourceType: "PortfolioPlan",
        resourceId: portfolioPlanId,
        relation: "given_access",
        subjectType: "Organization",
      },
      auth
    );

  const { data: advisorsData } = useFartherEmployees("NoEng", auth);

  const trueAdvisors = useMemo(
    () =>
      (advisorsData?.advisors ?? [])
        .filter((a) => a.roles.includes("Engineering") === false)
        .filter((a) => a.roles.includes("Client Ops") === false)
        .filter((a) => a.roles.some((r) => r.includes("Advisor"))),
    [advisorsData]
  );

  if (
    !advisorPermissionData ||
    !advisorsData ||
    !orgAccessData ||
    orgAccessIsLoading ||
    isMutating ||
    hasPublicBasePermission === null ||
    isNotAPrivatePlan === null
  ) {
    return <LoadingAccordionRow />;
  }

  const companyHasAccess = orgAccessData.some(
    (org) => org.subjectId === organization
  );

  const doesOrgHaveAccess =
    hasPublicBasePermission === true
      ? true
      : typeof organizationsHash[organization] === "boolean"
      ? organizationsHash[organization]
      : companyHasAccess;

  const totalAdvisorsByOrg = trueAdvisors.filter((advisor) => {
    return advisor.roles.some((role) => role.includes(organization));
  });

  const advisorsInOrg = totalAdvisorsByOrg.map(
    (a) => a.advisorId as unknown as PersonId
  );

  // Public plans will have a wildcard `*` in the person id
  const isPublic = (globalPermissionData.data ?? []).some(
    (d) => d.personId === "*"
  );

  const advisorsSharedTo = advisorPermissionData.filter((a) =>
    advisorsInOrg.includes(a.personId)
  );

  const allAdvisorsSharedToSate = orderBy(
    advisorsInOrg.map((a) => {
      const isSharedTo =
        advisorPermissionData.find((c) => c.personId === a)?.allowed === true;

      return {
        personId: a,
        allowed: isSharedTo,
      };
    }),
    [
      (a) => (a.allowed === true ? 1 : 0),
      (a): string =>
        trueAdvisors.find(
          (advisor) => String(advisor.advisorId) === String(a.personId)
        )?.name.first ?? "",
      (a): string =>
        trueAdvisors.find(
          (advisor) => String(advisor.advisorId) === String(a.personId)
        )?.name.last ?? "",
    ],
    ["desc", "asc", "asc"]
  );

  const advisorsInOrgCount = advisorsInOrg.length;

  // if public, all advisors are shared to it even if they are not in the list
  const advisorsSharedToCount = isPublic
    ? advisorsInOrgCount
    : advisorsSharedTo.length;

  const isLoading =
    advisorPermissionIsLoading ||
    orgAccessIsLoading ||
    isMutating ||
    globalPermissionData.isLoading;

  // if we have default people allowed even if the organization doesn't have permission, we should have permission true to this organization
  const defaultPermission =
    advisorsSharedToCount > 0 && doesOrgHaveAccess === false;

  const overAllPermission = doesOrgHaveAccess || defaultPermission;

  const tooltipText =
    // if the organization has default permission, we should not allow to toggle off
    !defaultPermission
      ? null
      : `This portfolio plan has default permissions and cannot be toggled off`;

  const collapseOrganizationAccordion = () => {
    if (overAllPermission === true) {
      onSelectedAccordion(organization);
    }
  };

  return (
    <Accordion
      style={{ backgroundColor: $surface }}
      expanded={organization === selectedAccordion}
      disabled={isNotAPrivatePlan}
      onChange={collapseOrganizationAccordion}
    >
      <AccordionSummary
        style={{ cursor: overAllPermission ? "pointer" : "default" }}
      >
        <Stack direction={"row"} justifyContent={"space-between"} flex={1}>
          <Stack direction={"row"} spacing={2}>
            <Text style={{ alignContent: "center" }} variant="body">
              {organization}
            </Text>
            <Stack
              border={`1px solid ${$borderBold}`}
              borderRadius={"5px"}
              p={"2px 6px"}
            >
              <Text
                style={{ alignContent: "center" }}
                variant="body"
              >{`${advisorsSharedToCount}/${advisorsInOrgCount} advisors`}</Text>
            </Stack>
          </Stack>
          <Tooltip placement={"top"} tooltipText={tooltipText}>
            <Stack direction={"row"} spacing={2} textAlign={"center"}>
              <>
                <Text style={{ alignContent: "center" }} variant="body">
                  {overAllPermission ? "Access Granted" : "Access Denied"}
                </Text>
                <Switch
                  disabled={isLoading || isNotAPrivatePlan || defaultPermission}
                  on={overAllPermission}
                  onClick={organizationSwitch(
                    organization,
                    doesOrgHaveAccess,
                    allAdvisorsSharedToSate
                  )}
                />
              </>
            </Stack>
          </Tooltip>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <ExpandableRowsAccordion
          data={allAdvisorsSharedToSate}
          isLoading={isLoading}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default OrganizationAccordion;
